import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeProvider } from "./theme-provider";
import { ToggleTheme } from "./toggle-theme";
import { Menu, X, Home, Info, FileText, DollarSign, MessageCircle, Phone } from 'lucide-react';

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const header = document.querySelector("header");
    if (header) setHeaderHeight(header.offsetHeight);

    const handleResize = () => {
      if (header) setHeaderHeight(header.offsetHeight);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileMenuOpen]);

  const scrollToSection = (id) => {
    const scrollFunction = () => {
      const element = document.getElementById(id);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - headerHeight,
          behavior: "smooth",
        });
      }
    };

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollFunction(), 100);
    } else {
      scrollFunction();
    }
    setMobileMenuOpen(false);
  };

  const navItems = [
    { name: "Home", path: "/", icon: <Home className="mr-2" /> },
    { name: "About", path: "/about", icon: <Info className="mr-2" /> },
    { name: "How We Work", path: "/how-we-work", icon: <FileText className="mr-2" /> },
    { name: "Plans", onClick: () => scrollToSection("pricing"), icon: <DollarSign className="mr-2" /> },
    { name: "Testimonials", onClick: () => scrollToSection("testimonials"), icon: <MessageCircle className="mr-2" /> },
    { name: "Contact", path: "/contact", icon: <Phone className="mr-2" /> },
  ];

  return (
    <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
      <div className="flex flex-col min-h-screen">
        <header className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 shadow-md">
          <nav className="container mx-auto px-4 py-4">
            <div className="flex justify-between items-center">
              <Link to="/" className="flex items-center">
                <img
                  src="/images/logo.png"
                  alt="Aditon Advertising Logo"
                  className="h-10 w-auto"
                />
              </Link>
              <div className="hidden md:flex items-center space-x-6">
                {navItems.map((item, index) => (
                  <NavItem key={index} item={item} location={location} />
                ))}
                <ToggleTheme />
              </div>
              <button
                className="md:hidden text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </nav>
        </header>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="md:hidden fixed inset-0 z-40 bg-white dark:bg-gray-900 overflow-y-auto"
            >
              <div className="container mx-auto px-4 py-4 flex justify-between items-center">
                <Link to="/" className="flex items-center">
                  <img
                    src="/images/logo.png"
                    alt="Aditon Advertising Logo"
                    className="h-10 w-auto"
                  />
                </Link>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <ul className="flex flex-col p-4 space-y-4">
                {navItems.map((item, index) => (
                  <li key={index}>
                    <NavItem item={item} location={location} mobile onClick={() => setMobileMenuOpen(false)} />
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        <main
          className="flex-grow pt-16 overflow-x-hidden overflow-y-auto"
          style={{ paddingTop: `${headerHeight}px` }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </ThemeProvider>
  );
};

const NavItem = ({ item, location, mobile, onClick }) => {
  const isActive = item.path ? location.pathname === item.path : false;

  const baseClasses = `relative px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ease-in-out ${
    mobile ? 'text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-300'
  }`;
  
  const activeClasses = `text-blue-600 dark:text-blue-400 ${
    !mobile && 'bg-blue-50 dark:bg-blue-900/30'
  }`;
  
  const hoverClasses = `hover:text-blue-600 dark:hover:text-blue-400 ${
    !mobile && 'hover:bg-blue-50 dark:hover:bg-blue-900/30'
  }`;

  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {item.path ? (
        <Link
          to={item.path}
          className={`${baseClasses} ${isActive ? activeClasses : hoverClasses} flex items-center`}
          onClick={onClick}
        >
          {mobile && item.icon}
          {item.name}
          {isActive && !mobile && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"
              layoutId="underline"
            />
          )}
        </Link>
      ) : (
        <button
          onClick={() => {
            item.onClick();
            if (onClick) onClick();
          }}
          className={`${baseClasses} ${hoverClasses} flex items-center`}
        >
          {mobile && item.icon}
          {item.name}
        </button>
      )}
    </motion.div>
  );
};

export default Layout;
